// Reask Model Configuration
export const REASK_MODEL_OPTIONS = [
  {
    id: "claude-opus-4-6",
    provider: "anthropic",
    model: "claude-opus-4-6",
    name: "Claude Opus 4.6"
  },
  {
    id: "claude-sonnet-4-6",
    provider: "anthropic",
    model: "claude-sonnet-4-6",
    name: "Claude Sonnet 4.6"
  },
  {
    id: "openai-gpt-5.2",
    provider: "openai",
    model: "gpt-5.2",
    name: "GPT 5.2"
  },
  {
    id: "openai-gpt-4o-mini",
    provider: "openai",
    model: "gpt-4o-mini",
    name: "GPT-4o Mini"
  },
  {
    id: "openai-o4-mini",
    provider: "openai",
    model: "o4-mini",
    name: "o4 Mini"
  },
  {
    id: "google-gemini-2.5-pro",
    provider: "google",
    model: "gemini-2.5-pro",
    name: "Gemini 2.5 Pro"
  },
  {
    id: "google-gemini-2.5-flash",
    provider: "google",
    model: "gemini-2.5-flash",
    name: "Gemini 2.5 Flash"
  },
  {
    id: "google-gemini-3",
    provider: "google",
    model: "gemini-3",
    name: "Gemini 3"
  },
];

// Function to get human-readable model name
export const formatModelName = (modelName) => {
  if (!modelName) return "Response";

  // First, try to find it in our predefined options (use human-readable names)
  const matchingOption = REASK_MODEL_OPTIONS.find(
    (option) =>
      option.model === modelName ||
      option.model === modelName.replace(/-\d{4}-\d{2}-\d{2}$/, "") // Try without date suffix
  );

  if (matchingOption) {
    return matchingOption.name;
  }

  // Fallback: Try to format unknown models intelligently
  let cleanName = modelName;

  // Handle GPT models
  if (cleanName.startsWith("gpt-")) {
    cleanName = cleanName
      .replace(/-\d{4}-\d{2}-\d{2}$/, "") // Remove date suffixes
      .replace(/-0\d{3}$/, "") // Remove version suffixes like -0125
      .replace(/^gpt-/, "GPT-") // Replace gpt- with GPT-
      .replace(/^(GPT-[^-]+)-(.+)/, (match, p1, p2) => {
        // Replace hyphens in the rest with spaces, but keep GPT-xxx intact
        return p1 + " " + p2.replace(/-/g, " ");
      })
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
  }
  // Handle Claude models
  else if (cleanName.startsWith("claude-")) {
    let matched = false;

    // Pattern 1: "claude-3-5-sonnet-20240620" (version-variant)
    let claudeMatch = cleanName.match(
      /^claude-(\d+)(?:-(\d+))?-(opus|sonnet|haiku)(?:-.*)?$/i
    );
    if (claudeMatch) {
      const [, major, minor, variant] = claudeMatch;
      const version = minor ? `${major}.${minor}` : major;
      const capitalizedVariant =
        variant.charAt(0).toUpperCase() + variant.slice(1).toLowerCase();
      cleanName = `Claude ${version} ${capitalizedVariant}`;
      matched = true;
    }

    // Pattern 2: "claude-4-opus-0" (version-variant, no minor)
    if (!matched) {
      claudeMatch = cleanName.match(
        /^claude-(\d+)-(opus|sonnet|haiku)(?:-.*)?$/i
      );
      if (claudeMatch) {
        const [, major, variant] = claudeMatch;
        const capitalizedVariant =
          variant.charAt(0).toUpperCase() + variant.slice(1).toLowerCase();
        cleanName = `Claude ${major} ${capitalizedVariant}`;
        matched = true;
      }
    }

    // Pattern 3: "claude-opus-4-5" (variant-version)
    if (!matched) {
      claudeMatch = cleanName.match(
        /^claude-(opus|sonnet|haiku)-(\d+)(?:-(\d+))?(?:-.*)?$/i
      );
      if (claudeMatch) {
        const [, variant, major, minor] = claudeMatch;
        const capitalizedVariant =
          variant.charAt(0).toUpperCase() + variant.slice(1).toLowerCase();
        const version = minor && minor !== "0" ? `${major}.${minor}` : major;
        cleanName = `Claude ${capitalizedVariant} ${version}`;
        matched = true;
      }
    }

    // Fallback for non-standard Claude names
    if (!matched) {
      cleanName = cleanName
        .replace(/^claude-/, "Claude ")
        .replace(/-\d{4}-\d{2}-\d{2}$/, "")
        .replace(/-20\d{6}$/, "")
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    }
  }
  // Handle Gemini models
  else if (cleanName.startsWith("gemini-")) {
    cleanName = cleanName
      .replace(/^gemini-/, "Gemini ")
      .replace(/-\d{4}-\d{2}-\d{2}$/, "") // Remove date suffixes
      .replace(/-20\d{6}$/, "") // Remove date suffixes
      .replace(/-exp$/, " Experimental") // Handle experimental models
      .replace(/-/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }
  // Handle other models
  else {
    cleanName = cleanName
      .replace(/-\d{4}-\d{2}-\d{2}$/, "") // Remove date suffixes
      .replace(/-20\d{6}$/, "") // Remove date suffixes
      .replace(/-/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  return cleanName;
};

// Function to get sort priority for responses (lower number = higher priority)
export const getResponseSortPriority = (response, originalResponseId) => {
  // First, check if it's the original response (either marked or the chronologically first one)
  if (response.isOriginalResponse || response.id === originalResponseId) {
    return 0;
  }

  // Then sort by model using the same order as REASK_MODEL_OPTIONS
  if (response.model) {
    // Normalize model name by removing date suffixes for matching
    const normalizedModel = response.model.replace(/-\d{4}-\d{2}-\d{2}$/, "");

    const modelIndex = REASK_MODEL_OPTIONS.findIndex(
      (option) =>
        option.model === normalizedModel || option.model === response.model
    );
    if (modelIndex !== -1) {
      return modelIndex + 1; // +1 to keep original response at 0
    }
    return 50; // Other models
  }

  // Fallback for responses without model info
  return 99;
};
